import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TableInstance, TableRowProps } from 'react-table';
import { style } from '../../../components/Shared/Style';
import GlobalFilter from '../../../components/Table/filterComponents/GlobalFilter';
import SortArrows from '../../../components/Table/sortArrows/SortArrows';
import { useGetTicketArchive } from '../hooks/ticketQueries';

const Styles = styled.div(({ theme }) => ({
    /* These styles are suggested for the table fill all available space in its containing element */
    // display: 'block',
    /* These styles are required for a horizontaly scrollable table overflow */

    '& .table': {
        borderSpacing: 0,
        '.tr': {
            columnGap: style.margin.m,
            borderBottom: `1px solid ${theme.colors.black50}`,
        },
        '.th': {
            color: theme.colors.secondary,
            padding: `0px 0px ${style.margin.m}px 0px`,
            fontSize: style.fontSize.epsilon,

            /* These styles are required for a scrollable body to align with the header properly */
            overflowX: 'hidden',
        },

        '.td': {
            fontSize: style.fontSize.centi,
            padding: '0px',
            overflowX: 'hidden',
        },

        '.tbody': {
            /* These styles are required for a scrollable table body */
            overflowX: 'hidden',
        },

        '.th, .td': {
            margin: 0,
            /* In this example we use an absolutely position resizer,
       so this is required. */

            position: 'relative',
            ':last-child': {
                paddingRight: '0px',
            },
        },
        '.td .tr div:first-of-type': {
            paddingLeft: '0px',
        },
    },
}));

const StyledLink = styled(Link)({
    textDecoration: 'none',
    color: 'inherit',
});

const getStyles = (
    props,
    style: {
        justifyContent?: string;
        alignItems?: string;
        display?: string;
    }
) => [
    props,
    {
        style: {
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            display: 'flex',
            ...style,
        },
    },
];

const headerProps = (props, { column }) => getStyles(props, column.align);

const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const FlexContainer = styled.div({
    display: 'flex',
    gap: '',
    alignItems: 'center',
});

export type Props<T extends Record<string, unknown>> = {
    useTable: TableInstance<T>;
    filterArea?: ReactNode;
    RowWrapper?: ReactNode;
};

type LinkRowProps = TableRowProps & {
    children: ReactNode;
};

type ArchiveTicketdata = NonNullable<ReturnType<typeof useGetTicketArchive>['data']>[0];

const Table = <T extends ArchiveTicketdata>({
    useTable,
    filterArea,
    ...rest
}: Props<T> & React.TableHTMLAttributes<HTMLTableElement>) => {
    const { getTableProps, headerGroups, rows, prepareRow, state, setGlobalFilter } = useTable;
    return (
        <Styles {...rest}>
            <div
                style={{
                    justifyContent: filterArea ? 'space-between' : 'end',
                    display: 'flex',
                    flexWrap: 'wrap',
                    width: '100%',
                    paddingBottom: '2rem',
                }}
            >
                {filterArea}
                <div>
                    <GlobalFilter placeholder="Sök" filter={state.globalFilter} setFilter={setGlobalFilter} />
                </div>
            </div>
            <div {...getTableProps()} className="table">
                <div>
                    {headerGroups.map((headerGroup) => (
                        <div
                            {...headerGroup.getHeaderGroupProps({})}
                            className="tr"
                            key={headerGroup.getFooterGroupProps().key}
                        >
                            {headerGroup.headers.map((column) => (
                                <div
                                    {...column.getHeaderProps(column.getSortByToggleProps(headerProps))}
                                    className="th"
                                    key={column.getHeaderProps().key}
                                >
                                    <FlexContainer>
                                        {column.render('Header')}
                                        {column.canSort && (
                                            <SortArrows
                                                activeSort={
                                                    column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : undefined
                                                }
                                            />
                                        )}
                                    </FlexContainer>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div className="tbody">
                    {rows.map((row) => {
                        prepareRow(row);
                        const currentRow = (
                            <StyledLink
                                to={`/tickets/archive/${row.original.id}`}
                                {...row.getRowProps()}
                                className="tr"
                                key={row.getRowProps().key}
                            >
                                {row.cells.map((cell) => {
                                    return (
                                        <div
                                            {...cell.getCellProps(cellProps)}
                                            className="td"
                                            key={cell.getCellProps().key}
                                        >
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </StyledLink>
                        );
                        return currentRow;
                    })}
                </div>
            </div>
        </Styles>
    );
};

export default Table;
